import React from "react";
import { Clock } from "lucide-react";

export const PRIORITY = {
  urgent: { label: "Urgent", badge: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
  high: { label: "High", badge: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  medium: { label: "Medium", badge: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  low: { label: "Low", badge: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
};

export const CATEGORY = {
  backlog: { label: "Backlog", chip: "bg-slate-100 text-slate-600 border-slate-200" },
  in_progress: { label: "In Progress", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  in_review: { label: "In Review", chip: "bg-sky-50 text-sky-700 border-sky-200" },
  completed: { label: "Completed", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export function initials(name = "") {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function isOverdue(iso, category) {
  if (!iso || category === "completed") return false;
  return new Date(iso).getTime() < Date.now();
}

export const STAGE_COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#64748b"];

export function stageColor(stage = {}) {
  if (stage.color) return stage.color;
  const map = { backlog: "#94a3b8", in_progress: "#f59e0b", in_review: "#0ea5e9", completed: "#10b981" };
  return map[stage.category] || "#94a3b8";
}

export function Dot({ className }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${className}`} />;
}

export const TICKET_STATUS = {
  open: { label: "Open", chip: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  in_progress: { label: "In Progress", chip: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  resolved: { label: "Resolved", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  closed: { label: "Closed", chip: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

const LABEL_PALETTE = [
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-sky-50 text-sky-700 border-sky-200",
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-teal-50 text-teal-700 border-teal-200",
];

export function labelColor(label = "") {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return LABEL_PALETTE[h % LABEL_PALETTE.length];
}

export function AgingBadge({ age = 0, inStage = null }) {
  const tone = age >= 14 ? "text-red-600 bg-red-50 border-red-200"
    : age >= 7 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-slate-500 bg-slate-50 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${tone}`}
      title={`${age}d old${inStage != null ? ` · ${inStage}d in current stage` : ""}`}>
      <Clock className="w-3 h-3" />{age}d
    </span>
  );
}

export function MentionText({ text = "", className = "" }) {
  const parts = text.split(/(@[A-Za-z0-9_.]+)/g);
  return (
    <span className={className}>
      {parts.map((p, i) => p.startsWith("@")
        ? <span key={i} className="text-indigo-600 font-semibold">{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>)}
    </span>
  );
}
